<template>
  <modal
    size="full"
    :title="title"
    :open="open"
    @close="closeModal"
  >
    <div class="grid">
      <section>
        <div v-if="card">
          <text-input
            id="search"
            v-model="searchQuery"
            label="Search"
            placeholder="Search for a card"
          />

          <div class="search-results">
            <align class="results-header" justify="space-between">
              <align small-gap>
                <h5>Search Results</h5>
                <icon-button
                  as="router-link"
                  target="_blank"
                  :to="salesHistoryLink"
                  icon="open_in_new"
                  size="16"
                  tooltip="Open in new tab"
                />
              </align>

              <align small-gap>
                <icon-button icon="insert_photo" :class="{ success: salesView === 'images' }" @click="salesView = 'images'; fetchSales(); viewSales = true" />
                <icon-button icon="cl_psa_image" :class="{ success: salesView === 'psa_images' }" @click="salesView = 'psa_images'; fetchSales(); viewSales = true" />
                <icon-button icon="receipt_long" :class="{ success: salesView === 'sales' }" @click="salesView = 'sales'; fetchSales(); viewSales = true" />
                <icon-button :icon="viewSales ? 'expand_less' : 'expand_more'" @click="viewSales = !viewSales" />
              </align>
            </align>

            <div v-if="viewSales">
              <div v-if="loadingSales" class="loading-container">
                <spinner inline />
              </div>

              <div v-else-if="searchResults.length > 0" class="search-scroll-container">
                <div v-if="salesView === 'sales'">
                  <sales-list-item
                    v-for="item in searchResults"
                    :key="item.itemId"
                    :item="item"
                    as="a"
                    split
                    :href="item.url"
                    target="_blank"
                  />
                </div>

                <div v-else-if="salesView === 'images'" class="possible-images">
                  <div class="grid">
                    <div
                      v-for="item in searchResults"
                      :key="item.itemId"
                      class="possible-image"
                      @click="selectImage(item.image)"
                    >
                      <img :src="item.image" />

                      <div v-if="item.imageDimensions" class="dimensions">
                        {{ item.imageDimensions.width }}x{{ item.imageDimensions.height }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>


              <div class="search-scroll-container">
                <div v-if="salesView === 'psa_images'" class="possible-images">
                  <div class="grid">
                    <div
                      v-for="image in psaImageResults"
                      :key="image.id"
                      class="possible-image"
                      @click="selectImage(image.url)"
                    >
                      <img :src="image.url" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="card">
            <card-image-upload v-model="card" @result="handleUpload" />
          </div>
        </div>
      </section>

      <section>
        <div v-if="card">
          <card-details-fields v-model="card" />

          <card-advanced-fields v-model="card" @selectImage="selectImage" />

          <div v-if="editing && card" class="dates">
            <div>Added: {{ $filters.date(card.dateAdded) }}</div>
            <br />
          </div>
        </div>
      </section>
    </div>

    <template #footer>
      <align justify="space-between">
        <div class="button-row">
          <btn
            :disabled="disabled"
            :loading="saving"
            @click="submit"
          >
            Save
          </btn>

          <btn
            v-if="canDelete"
            kind="error"
            loading-text="Deleting"
            :loading="deleting"
            @click="deleteCard"
          >
            Delete
          </btn>

          <btn
            v-else
            kind="secondary"
            @click="resetForm"
          >
            Reset
          </btn>
        </div>

        <btn
          kind="ebay"
          :disabled="disableGenerateQuery"
          @click="generateEbayQuery"
        >
          Generate Query
        </btn>
      </align>
    </template>
  </modal>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import clone from 'lodash.clonedeep';
import debounce from 'lodash.debounce';
import firebase from 'firebase/compat/app';
import * as Sentry from '@sentry/vue';

import { cardService, searchService, imageService } from '@/services';

import CardImageUpload from '@/cards/CardImageUpload';
import CardDetailsFields from '@/cards/CardDetailsFields';
import CardAdvancedFields from '@/cards/CardAdvancedFields';
import SalesListItem from '@cardladder/shared/components/SalesListItem';

import { DEFAULT_CARD_DETAILS } from '@cardladder/shared/constants';

import {
  getSuggestedQuery,
  replaceGradeInQuery,
} from '@cardladder/shared/utils/query.util';

import { getGraderFromCondition } from '@cardladder/shared/utils/format.util';

export default {
  components: {
    CardImageUpload,
    CardDetailsFields,
    CardAdvancedFields,
    SalesListItem,
  },

  data() {
    return {
      open: false,
      id: null,
      card: null,
      saving: false,
      loaded: false,
      loadingSales: false,
      deleting: false,
      searchResults: [],
      psaImageResults: [],
      viewSales: false,
      salesView: 'images',
      searchQuery: '',
    };
  },

  mounted() {
    this.debounce_fetchSales = debounce(this.fetchSales, 1000);

    this.$bus.$on('admin-edit-card', this.handleEdit);
    this.$bus.$on('admin-copy-card', this.handleCopy);
    this.$bus.$on('keyboard-shortcut-s', this.handleKeyboardSave);
  },

  beforeDestroy() {
    this.$bus.$off('admin-edit-card', this.handleEdit);
    this.$bus.$off('admin-copy-card', this.handleCopy);
    this.$bus.$off('keyboard-shortcut-s', this.handleKeyboardSave);
  },

  watch: {
    'searchQuery': {
      handler(value) {
        this.debounce_fetchSales();
        this.viewSales = Boolean(value);
      },
    },

    'card.ebayQuery': {
      handler() {
        this.debounce_fetchSales();
      },
    },

    'card.condition': {
      handler(newCondition, oldCondition) {
        if (newCondition && oldCondition) {
          this.card.ebayQuery = replaceGradeInQuery(this.card.ebayQuery, newCondition, oldCondition);
        }
      },
    },

    'card.player': {
      handler(value) {
        if (!value) {
          this.card.playerIndexId = '';
        }
      },
    },
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
    }),

    ...mapGetters({
      isAdmin: 'isAdmin',
    }),

    canDelete() {
      return this.editing && (this.isAdmin || this.card?.hidden);
    },

    editing() {
      return Boolean(this.id);
    },

    title() {
      return this.editing ? 'Edit Card' : 'Add Card';
    },

    disabled() {
      if (!this.card) {
        return true;
      }

      if (!this.card.playerIndexId && this.card.player.includes('&')) {
        return true;
      }

      return !this.card.player || !this.card.condition || !this.card.year || !this.card.set || !this.card.category;
    },

    disableGenerateQuery() {
      if (!this.card) {
        return true;
      }

      return !this.card.player || !this.card.category || !this.card.condition || !this.card.year;
    },

    salesHistoryLink() {
      return `/sales-history?q=${encodeURIComponent(this.card.ebayQuery || this.searchQuery)}`;
    },
  },

  methods: {
    handleEdit(id) {
      this.id = id;

      this.load();
      this.open = true;
    },

    async handleCopy(card) {
      const defaultCard = clone(DEFAULT_CARD_DETAILS);
      const cardFieldsToCopy = {
        player: card.player || '',
        playerIndexId: card.playerIndexId || '',
        year: card.year || '',
        set: card.set || '',
        variation: card.variation || '',
        number: card.number || '',
        category: card.category || '',
        condition: card.condition || '',
        employee: card.employee || '',
        ebayQuery: card.ebayQuery || '',
        setInformation: card.setInformation || '',
        setInformationLinkUrl1: card.setInformationLinkUrl1 || '',
        setInformationLinkDescription1: card.setInformationLinkDescription1 || '',
        cardInformation: card.cardInformation || '',
        cardInformationLinkUrl1: card.cardInformationLinkUrl1 || '',
        cardInformationLinkDescription1: card.cardInformationLinkDescription1 || '',
      };

      this.card = {
        ...defaultCard,
        ...cardFieldsToCopy,
      };

      if (card.imageUrl) {
        const { base64Url, blob } = await imageService.getImageFromUrl(card.imageUrl);

        this.card.image = blob;
        this.handleUpload(base64Url);
      }

      this.open = true;
      this.loaded = true;
      this.id = null;
    },

    async load() {
      if (this.editing) {
        try {
          this.card = await cardService.getCard(this.id);
          this.searchResults = [];
          this.psaImageResults = [];
        } catch (e) {
          this.$toasted.error('Error loading the card');
          Sentry.captureException(e);
        }
      } else {
        this.card = {
          ...clone(DEFAULT_CARD_DETAILS),
          employee: this.user.uid,
        };
      }

      this.loaded = true;
    },

    async submit() {
      this.saving = true;
      this.card.grader = getGraderFromCondition(this.card.condition);
      this.card.uid = this.user.uid;

      if (!this.card.playerIndexId) {
        this.card.playerIndexId = this.card.player;
      }

      try {
        const id = await cardService.createOrUpdate(this.card);

        if (!this.editing) {
          this.$router.push({
            path: `/card/${id}`,
            query: {
              init: true,
              showSales: true,
            },
          });
        }

        this.$toasted.success('Card saved!');

        this.closeModal();
      } catch (e) {
        this.$toasted.error('Error saving the card');
        Sentry.captureException(e);
      }

      this.saving = false;
    },

    handleKeyboardSave() {
      if (this.open) {
        this.submit();
      }
    },

    async deleteCard() {
      this.deleting = true;

      if (this.card.id && window.confirm('Are you sure you want to delete this card?')) {
        try {
          await cardService.deleteCard(this.card.id, this.user.uid);

          setTimeout(() => {
            this.$toasted.success('Card has been deleted!');
            this.$router.replace(this.$route.path.includes('/add') || this.$route.query.type === 'add' ? '/add' : '/ladder');
            this.$bus.$emit('add-cards-changed');
            this.deleting = false;
            this.closeModal();
          }, 4000);
        } catch (e) {
          this.$toasted.error('Failed to delete the card');
          Sentry.captureException(e);
        }
      }
    },

    async handleUpload(uploadedImage) {
      if (uploadedImage) {
        let base64Image = uploadedImage.toString().replace(/^data:(.*,)?/, '');

        if ((base64Image.length % 4) > 0) {
          base64Image += '='.repeat(4 - (base64Image.length % 4));
        }

        const { data: imagePropertiesResponse } = await firebase.functions().httpsCallable('httpcardimagedetection')({ base64Image });

        if (imagePropertiesResponse && imagePropertiesResponse.properties && imagePropertiesResponse.properties.cert && imagePropertiesResponse.properties.grader) {
          const { data: cardDataFromCert } = await firebase.functions().httpsCallable('httpbuildcollectioncard')({
            cert: imagePropertiesResponse.properties.cert,
            grader: imagePropertiesResponse.properties.grader,
          });

          this.card = {
            ...this.card,
            player: this.card.player || cardDataFromCert.player,
            year: this.card.year || cardDataFromCert.year,
            set: this.card.set || cardDataFromCert.set,
            variation: this.card.variation || cardDataFromCert.variation,
            number: (this.card.number || cardDataFromCert.number).trim(),
            condition: this.card.condition || cardDataFromCert.condition || 'Raw',
            cert: imagePropertiesResponse.properties.cert || this.card.cert || '',
            category: this.card.category || cardDataFromCert.category || '',
          };

          if (cardDataFromCert.profileId && cardDataFromCert.grader && cardDataFromCert.grade) {
            this.card.profileId = cardDataFromCert.profileId;
            this.card.grader = cardDataFromCert.grader;
            this.card.grade = cardDataFromCert.grade;
            this.card.autographGrade = cardDataFromCert.autographGrade;
            this.card.qualifierType = cardDataFromCert.qualifierType;
            this.card.hasAutographGrade = cardDataFromCert.hasAutographGrade;
            this.card.hasQualifier = cardDataFromCert.hasQualifier;
            this.card.pop = cardDataFromCert.pop;
            this.card.popLastUpdated = new Date();
          }

          if (imagePropertiesResponse.properties.certVertices || imagePropertiesResponse.properties.cardSerialVertices) {
            try {
              this.card.image = await this.blackOutCardImageSerials(
                uploadedImage,
                imagePropertiesResponse.properties.certVertices,
                imagePropertiesResponse.properties.cardSerialVertices,
              );
            } catch (e) {
              // swallow
            }
          }
        }
      }
    },

    async fetchSales() {
      if (this.salesView === 'psa_images') {
        this.fetchPsaImages();
        return;
      }

      if (!this.searchQuery && !this.card.ebayQuery) return;

      this.loadingSales = true;

        try {
          const data = await searchService.search({
            index: 'salesarchive',
            query: this.card.ebayQuery || this.searchQuery,
            page: 0,
            limit: 100,
            sort: 'date',
          });

          const searchResults = await Promise.all(data.hits
            .filter(({ image }) => this.salesView === 'sales' || Boolean(image))
            .map(async(hit) => {
              const imageDimensions = await imageService.getImageDimensions(hit.image);

              return {
                ...hit,
                imageDimensions,
              };
            }),
          );

          this.searchResults = searchResults;
        } catch (e) {
          Sentry.captureException(e);
        }

        this.loadingSales = false;
    },

    async fetchPsaImages() {
      this.loadingSales = true;

      try {
        const { data } = await firebase.functions().httpsCallable('httpPsaAzureToken')();
        const params = { token: data.token };

        if (getGraderFromCondition(this.card.condition) === 'psa') {
          params.psaGrade = parseFloat(this.card.condition.match(/[\d.]+/));
        }

        if (this.card.cert) {
          const { data } = await firebase.functions().httpsCallable('httpPsaGetSpecFromCert')({ cert: this.card.cert });
          params.specNumber = data.specNumber;
        }

        if (this.card.set && this.card.player) {
          params.search = `${this.card.year} ${this.card.set} ${this.card.player} ${this.card.number} ${this.card.variation}`;
        } else if (this.searchQuery) {
          params.search = this.searchQuery;
        }

        const { data: images } = await firebase.functions().httpsCallable('httpPsaImageSearch')(params);

        this.psaImageResults = images;
      } catch (e) {
        Sentry.captureException(e);
      }

      this.loadingSales = false;
    },


    async blackOutCardImageSerials(imageData, certVertices, cardSerialVertices) {
      return new Promise((resolve, reject) => {
        try {
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');

          const image = new Image();

          image.onload = async() => {
            canvas.height = image.height;
            canvas.width = image.width;
            context.drawImage(image, 0, 0);

            if (certVertices) {
              context.beginPath();

              certVertices.forEach((vertex, i) => {
                if (i === 0) {
                  context.moveTo(vertex.x, vertex.y);
                } else {
                  context.lineTo(vertex.x, vertex.y);
                }
              });

              context.fill();
            }

            if (cardSerialVertices) {
              context.beginPath();

              if (cardSerialVertices.length === 4) {
                const isVertical = Math.abs(cardSerialVertices[0].x - cardSerialVertices[1].x) < Math.abs(cardSerialVertices[1].x - cardSerialVertices[2].x);

                context.moveTo(cardSerialVertices[0].x, cardSerialVertices[0].y);
                context.lineTo((cardSerialVertices[0].x + cardSerialVertices[1].x) / 2, (cardSerialVertices[0].y + cardSerialVertices[1].y) / 2);
                context.lineTo(isVertical ? cardSerialVertices[2].x : (cardSerialVertices[2].x + cardSerialVertices[3].x) / 2, isVertical ? (cardSerialVertices[2].y + cardSerialVertices[3].y) / 2 : cardSerialVertices[2].y);
                context.lineTo(cardSerialVertices[3].x, cardSerialVertices[3].y);
              } else {
                cardSerialVertices.forEach((vertex, i) => {
                  if (i === 0) {
                    context.moveTo(vertex.x, vertex.y);
                  } else {
                    context.lineTo(vertex.x, vertex.y);
                  }
                });
              }

              context.fill();
            }

            const blob = await new Promise(resolve => canvas.toBlob(resolve));

            resolve(blob);
          };

          image.src = imageData;
        } catch (e) {
          reject(e);
        }
      });
    },

    async selectImage(image) {
      const { base64Url, blob } = await imageService.getImageFromUrl(image);

      this.card.image = blob;
      this.handleUpload(base64Url);
      this.viewSales = false;
    },

    closeModal() {
      this.card = clone(DEFAULT_CARD_DETAILS);

      this.searchQuery = '';
      this.searchResults = [];
      this.psaImageResults = [];
      this.id = null;
      this.loaded = false;

      this.open = false;
    },

    generateEbayQuery() {
      let confirm = true;

      if (this.card.ebayQuery) {
        confirm = window.confirm('This will wipe out your existing query. Are you sure you want to generate a new one?');
      }

      if (confirm) {
        this.card.ebayQuery = getSuggestedQuery(this.card);
      }
    },

    resetForm() {
      if (window.confirm('Are you sure you want to reset this form?')) {
        this.searchQuery = '';
        this.searchResults = [];
        this.load();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.grid {
  display: grid;
  grid-gap: $gp;
  grid-template-columns: repeat(2, 1fr);
  margin-bottom: $gp;

  @media($medium-down) {
    display: block;
  }
}

.search-results {
  border: solid 1px var(--theme-input-border);
  margin: $gp 0;
  border-radius: $border-radius;
  overflow: hidden;
  display: block;

  .results-header {
    padding: $gp;

    h5 {
      margin-bottom: 0;
    }
  }
}

.possible-images {
  padding: 0 $gp;
}

.possible-image {
  position: relative;

  .dimensions {
    color: $color-white;
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    text-align: center;
    font-size: 12px;
    font-weight: 900;
    text-shadow: 0 0 3px black;
    padding: 5px;
  }

  &:hover {
    cursor: pointer;
  }
}

.card-details-fields {
  margin-bottom: $gp;
}

.search-scroll-container {
  @include overflowY;
  height: 100%;
  max-height: 600px;
}

.loading-container {
  padding: $gp;
  text-align: center;
}
</style>
